// src/components/Sidebar.js
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";

import {
  LuBoxes,
  LuTruck,
  LuUser,
  LuHandshake,
  LuMap,
  LuMapPin,
  LuFileText,
  LuBell,
  LuSettings,
  LuLogOut,
  LuLayoutDashboard,
  LuCalendarDays,
  LuSun,
  LuMoon,
} from "react-icons/lu";

import "../styles/Sidebar.css";
import "../styles/SidebarThemeSwitch.css";
import defaultLogo from "../assets/Logo.png";
import halloweenLogo from "../assets/HALLOWEEN.png";
import api from "../axiosConfig";
import { hardLogout } from "../utils/auth";
import {
  applyAppearance,
  getStoredAppearance,
  saveAppearance,
} from "../utils/appearancePreference";
import { useTranslation } from "react-i18next";
import { getSocket } from "../lib/socket";
import { getStoredToken, getStoredUser } from "../lib/authStorage";
import { prefetchVehiclesSummaries } from "../lib/prefetchVehicles";
import {
  prefetchPersonnelFoundation,
  getPersonnelPrefetchScopeFromPath,
} from "../lib/prefetchPersonnel";

import usePersonnelPermissions from "../hooks/usePersonnelPermissions";
import usePersonnelPendingCount from "../hooks/usePersonnelPendingCount";

import {
  prefetchLoadsSummaries,
  prefetchDriversSummaries,
  prefetchClientsSummaries,
  prefetchBillingSummaries,
  prefetchPlacesList,
  prefetchInvoicesList,
} from "../lib/prefetchSidebarLists";

// --- permissions helpers ---
const MATRIX_STORAGE_KEY = "permissions.matrix.v1";
const readPermMatrix = () => {
  try {
    return JSON.parse(localStorage.getItem(MATRIX_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const can = (role, key) => {
  const normalizedRole =
    String(
      role ||
        ""
    )
      .trim()
      .toLowerCase();

  if (
    normalizedRole ===
    "admin"
  ) {
    return true;
  }

  const m =
    readPermMatrix();

  if (
    !normalizedRole ||
    !m[normalizedRole]
  ) {
    return true;
  }

  const v =
    m[normalizedRole][key];

  return typeof v ===
    "boolean"
    ? v
    : true;
};

/* ------------------------------- helpers ------------------------------- */
const readJSON = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "{}");
  } catch {
    return {};
  }
};

const writeJSON = (key, val) => {
  try {
    localStorage.setItem(key, JSON.stringify(val || {}));
  } catch {}
};

// Estado local de notificações do dispatcher (mesma chave que em DispatcherNotifications.js)
const DISPATCHER_LOCAL_STORAGE_KEY = "dispatcherNotifPrefs";

const NOTIFS_KEY = "app.notifications";

function getNotifsActive() {
  const v = window.localStorage.getItem(NOTIFS_KEY);
  return v ? v === "active" : true; // default = ativo
}

const readNotifLocalState = () => {
  try {
    const raw = window.localStorage.getItem(DISPATCHER_LOCAL_STORAGE_KEY);
    if (!raw) {
      return { readIds: new Set(), hiddenIds: new Set() };
    }
    const parsed = JSON.parse(raw);
    return {
      readIds: new Set(parsed.readIds || []),
      hiddenIds: new Set(parsed.hiddenIds || []),
    };
  } catch (e) {
    console.warn("[Sidebar] falha ao ler estado local de notificações", e);
    return { readIds: new Set(), hiddenIds: new Set() };
  }
};

const computeInitial = ({ name, email }) => {
  const base = (name && name.trim()) || (email && email.trim()) || "U";
  return base.charAt(0).toUpperCase();
};

// Absolutiza URLs relativas vindas do backend (p.ex. "/uploads/...") usando o baseURL do axios
const absoluteFromApiBase = (p) => {
  if (!p || typeof p !== "string") return null;
  if (/^https?:\/\//i.test(p)) return p; // já é absoluta
  const base = (api.defaults?.baseURL || "").replace(/\/$/, "");
  if (!base) return p;
  return `${base}${p.startsWith("/") ? "" : "/"}${p}`;
};

// Cobre várias variantes de nomes de campo possíveis para a foto de perfil
const pickAvatarFrom = (obj) =>
  obj?.profilePictureThumbUrl ||
  obj?.profilePictureUrl ||
  obj?.profilePicture ||
  obj?.photoThumbUrl ||
  obj?.photoUrl ||
  obj?.photo ||
  null;

// Normaliza resposta de /auth/me e /users/profile
const normalizeMe = (data) => {
  const u = data?.user || data?.profile || data?.me || data || {};
  const ua = data?.userAuth || data?.auth || {};
  const name = u.name || u.fullName || u.username || data?.name || "";
  const email = ua.email || u.email || data?.email || "";
  const role = u.role || ua.role || data?.role || null;
  const rawAvatar = pickAvatarFrom(data) || pickAvatarFrom(u);
  const avatarUrl = absoluteFromApiBase(rawAvatar);
  return { name, email, avatarUrl, role };
};

// Notification count cache scoped by company and user.
const getSidebarNotifCacheKey = () => {
  try {
    const token = String(getStoredToken() || "")
      .replace(/^Bearer\s+/i, "");

    let claims = {};

    if (token.includes(".")) {
      let encoded = token.split(".")[1]
        .replace(/-/g, "+")
        .replace(/_/g, "/");

      encoded += "=".repeat((4 - encoded.length % 4) % 4);

      claims = JSON.parse(atob(encoded));
    }

    const user = getStoredUser() || {};

    const companyId =
      claims.company_id ??
      claims.companyId ??
      user.company_id ??
      user.companyId;

    const userId =
      claims.user_id ??
      claims.userId ??
      claims.sub ??
      user.id ??
      user.userId;

    if (companyId == null || userId == null) {
      return null;
    }

    return `loadevo.sidebar.unread.v1:${companyId}:${userId}`;
  } catch {
    return null;
  }
};

const readSidebarNotifCount = () => {
  try {
    const key = getSidebarNotifCacheKey();
    if (!key) return null;

    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;

    const count = Number(raw);

    return Number.isInteger(count) && count >= 0
      ? count
      : null;
  } catch {
    return null;
  }
};

const writeSidebarNotifCount = (count) => {
  try {
    const key = getSidebarNotifCacheKey();

    if (key) {
      window.localStorage.setItem(key, String(count));
    }
  } catch {}
};

const getSeasonalLogo = (date = new Date()) => {
  const month = date.getMonth() + 1;
  const day = date.getDate();

  // Halloween: 23 a 31 de outubro
  if (month === 10 && day >= 23 && day <= 31) {
    return halloweenLogo;
  }

  // Logótipo habitual
  return defaultLogo;
};

function Sidebar({ user: userProp }) {
  const { t } = useTranslation("side_bar");
  const location = useLocation();

  const [logo, setLogo] = useState(getSeasonalLogo);

  useEffect(() => {
    const refreshLogo = () => {
      setLogo(getSeasonalLogo());
    };

    // Atualizar periodicamente, mesmo sem recarregar a página
    const interval = window.setInterval(refreshLogo, 60 * 1000);

    return () => window.clearInterval(interval);
  }, []);
  const [notifCount, setNotifCount] = useState(readSidebarNotifCount);
  const [notificationsActive, setNotificationsActive] = useState(getNotifsActive);

  // Ler o tema efetivamente apresentado, incluindo preferência "Sistema".
  const readDarkMode = React.useCallback(() => {
    if (typeof document !== "undefined") {
      const activeTheme = document.documentElement.getAttribute("data-theme");

      if (activeTheme === "dark") return true;
      if (activeTheme === "light") return false;
    }

    const preference = getStoredAppearance();

    if (preference === "dark") return true;
    if (preference === "light") return false;

    return (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-color-scheme: dark)")?.matches === true
    );
  }, []);

  const [isDarkMode, setIsDarkMode] = useState(readDarkMode);

  useEffect(() => {
    const syncTheme = () => {
      setIsDarkMode(readDarkMode());
    };

    // Sincronizar com alterações aplicadas em outras partes da aplicação.
    const observer = new MutationObserver(syncTheme);

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    window.addEventListener("app:appearance:changed", syncTheme);

    // Se estiver selecionado "Sistema", acompanhar o tema do dispositivo.
    const mediaQuery = window.matchMedia?.("(prefers-color-scheme: dark)");

    const onSystemChange = () => {
      if (getStoredAppearance() === "system") {
        applyAppearance("system");
        syncTheme();
      }
    };

    mediaQuery?.addEventListener?.("change", onSystemChange);

    syncTheme();

    return () => {
      observer.disconnect();
      window.removeEventListener("app:appearance:changed", syncTheme);
      mediaQuery?.removeEventListener?.("change", onSystemChange);
    };
  }, [readDarkMode]);

  const toggleSidebarTheme = () => {
    const nextAppearance = isDarkMode ? "light" : "dark";

    saveAppearance(nextAppearance);
    applyAppearance(nextAppearance);
    setIsDarkMode(nextAppearance === "dark");

    window.dispatchEvent(
      new CustomEvent("app:appearance:changed", {
        detail: { value: nextAppearance },
      })
    );
  };

  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const navigate = useNavigate();

  const notifRequestSeqRef = useRef(0);

  const commitNotifCount = React.useCallback((count) => {
    const normalized = Number(count);

    if (!Number.isInteger(normalized) || normalized < 0) {
      return;
    }

    setNotifCount((previous) =>
      previous === normalized ? previous : normalized
    );

    writeSidebarNotifCount(normalized);
  }, []);

  const refreshNotifCount = React.useCallback(async () => {
    if (!getNotifsActive()) return;

    const requestId = ++notifRequestSeqRef.current;
    const requestScope = getSidebarNotifCacheKey();

    try {
      const { data } = await api.get("/notifications4dispatcher");

      // Ignore superseded responses and account changes.
      if (
        requestId !== notifRequestSeqRef.current ||
        requestScope !== getSidebarNotifCacheKey() ||
        !getNotifsActive()
      ) {
        return;
      }

      const arr = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
          ? data
          : [];

      const { readIds, hiddenIds } = readNotifLocalState();

      const read = new Set([...readIds].map(String));
      const hidden = new Set([...hiddenIds].map(String));

      const unread = arr.filter((notification) => {
        const id = String(notification.id);

        if (hidden.has(id) || read.has(id)) {
          return false;
        }

        return !notification.readAt;
      }).length;

      commitNotifCount(unread);
    } catch (error) {
      console.error(
        "[Sidebar] erro ao atualizar notificações:",
        error
      );

      // Keep the last confirmed count if the request fails.
    }
  }, [commitNotifCount]);

  const {
    hasAnyView:
      hasAnyPersonnelView,

    firstAllowedPath:
      firstAllowedPersonnelPath,

    can:
      canPersonnel,
  } =
    usePersonnelPermissions();

  const canViewPersonnelRequests =
    canPersonnel(
      "personnelRequestsView"
    );

  const personnelPendingCount =
    usePersonnelPendingCount({
      enabled:
        hasAnyPersonnelView &&
        canViewPersonnelRequests,

      listenRealtime:
        true,
    });

  const [userInfo, setUserInfo] = useState({
    name: "",
    email: "",
    avatarUrl: null,
    role: null,
  });

  // 0) seed inicial: prop -> localStorage(auth_user) -> localStorage(user)
  useEffect(() => {
    const fromProp =
      userProp && (userProp.name || userProp.email)
        ? {
            name: userProp.name || userProp.fullName || userProp.username || "",
            email: userProp.email || "",
            avatarUrl: absoluteFromApiBase(pickAvatarFrom(userProp)) || null,
            role: userProp.role || null,
          }
        : null;

    const fromAuthUser = readJSON("auth_user");
    const fromUser = readJSON("user"); // guardado no login atual

    const seed =
      fromProp ||
      (fromAuthUser.name || fromAuthUser.email ? fromAuthUser : null) ||
      (fromUser.name || fromUser.email
        ? {
            name: fromUser.name || fromUser.fullName || fromUser.username || "",
            email: fromUser.email || "",
            role: fromUser.role || null,
            avatarUrl: absoluteFromApiBase(pickAvatarFrom(fromUser)) || null,
          }
        : null) || {
        name: t("profile.default_user"),
        email: "",
        avatarUrl: null,
        role: null,
      };

    setUserInfo(seed);
  }, [userProp, t]);

  useEffect(() => {
    const run = () => {
      prefetchVehiclesSummaries();
      prefetchLoadsSummaries();         // ✅ vai escrever na cache certa (vamos ajustar a lib)
      prefetchPlacesList().catch(() => {});
      prefetchInvoicesList().catch(() => {});
      prefetchClientsSummaries().catch(() => {});
    };

    // ✅ não atrapalha o first paint
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const id = window.requestIdleCallback(run, { timeout: 1200 });
      return () => window.cancelIdleCallback?.(id);
    }

    const t = setTimeout(run, 250);
    return () => clearTimeout(t);
  }, []);

  // 1) carregar perfil a partir da rota real existente no backend
  useEffect(() => {
    let cancelled = false;

    const fetchMe = async () => {
      try {
        const { data } = await api.get("/users/profile");
        const next = normalizeMe(data);

        if (!cancelled && (next.name || next.email)) {
          setUserInfo((prev) => ({ ...prev, ...next }));
          writeJSON("auth_user", next);
          return;
        }
      } catch (e) {
        console.warn(
          "[Sidebar] falha ao carregar /users/profile:",
          e?.response?.status || e?.message
        );
      }

      const ls = readJSON("auth_user");
      if (!cancelled && (ls.name || ls.email)) {
        setUserInfo(ls);
      }
    };

    fetchMe();

    return () => {
      cancelled = true;
    };
  }, []);

  // 2) live-update quando PersonalInfo guardar
  useEffect(() => {
    const onProfileSaved = async (e) => {
      const { name, email } = e.detail || {};
      try {
        const { data } = await api.get("/users/profile");
        const next = normalizeMe(data);
        const merged = {
          name: name || next.name || userInfo.name || t("profile.default_user"),
          email: email || next.email || userInfo.email || "",
          avatarUrl: next.avatarUrl ?? userInfo.avatarUrl ?? null,
          role: next.role ?? userInfo.role ?? null,
        };
        setUserInfo(merged);
        writeJSON("auth_user", merged);
      } catch {
        const merged = {
          name: name || userInfo.name || t("profile.default_user"),
          email: email || userInfo.email || "",
          avatarUrl: userInfo.avatarUrl || null,
          role: userInfo.role || null,
        };
        setUserInfo(merged);
        writeJSON("auth_user", merged);
      }
    };
    window.addEventListener("profileSaved", onProfileSaved);
    return () => window.removeEventListener("profileSaved", onProfileSaved);
  }, [userInfo, t]);

  // 3) refletir alterações do localStorage vindas de outro separador
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "auth_user") {
        const v = readJSON("auth_user");
        setUserInfo({
          name: v.name || t("profile.default_user"),
          email: v.email || "",
          avatarUrl: v.avatarUrl || null,
          role: v.role || null,
        });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [t]);

  // re-render on permission matrix change
  const [, setTick] = useState(0);
  useEffect(() => {
    const onPermsChanged = () => setTick((x) => x + 1);
    window.addEventListener("permissions:changed", onPermsChanged);
    return () => window.removeEventListener("permissions:changed", onPermsChanged);
  }, []);

  useEffect(() => {
    const onChanged = (e) => {
      const next = !!e?.detail?.active;
      setNotificationsActive(next);

      // Keep the last count in memory.
      // The badge is already hidden by notificationsActive.
    };

    window.addEventListener("app:notifications:changed", onChanged);
    return () => window.removeEventListener("app:notifications:changed", onChanged);
  }, []);

  // Fetch on mount, then quietly revalidate every 30 seconds.
  useEffect(() => {
    if (!notificationsActive) return;

    void refreshNotifCount();

    const id = window.setInterval(() => {
      void refreshNotifCount();
    }, 30000);

    return () => {
      window.clearInterval(id);
      notifRequestSeqRef.current += 1;
    };
  }, [notificationsActive, refreshNotifCount]);

  // 🔔 WebSocket: atualizar badge em tempo real
  useEffect(() => {
    if (!notificationsActive) return;

    const socket = getSocket();

    const subscribe = () => {
      if (socket.connected) {
        socket.emit("subscribe:loads");
      }
    };

    let notifRefreshTimer = null;

    const scheduleNotifRefresh = () => {
      if (!getNotifsActive()) return;

      if (notifRefreshTimer !== null) {
        window.clearTimeout(notifRefreshTimer);
      }

      // Coalesce repeated WebSocket events into one API request.
      notifRefreshTimer = window.setTimeout(() => {
        notifRefreshTimer = null;
        void refreshNotifCount();
      }, 120);
    };

    const handleNewDispatcherNotif = () => {
      scheduleNotifRefresh();
    };

    const handleDispatcherStale = () => {
      scheduleNotifRefresh();
    };

    socket.on("connect", subscribe);
    socket.on("reconnect", subscribe);

    if (socket.connected) {
      subscribe();
    }

    socket.on("dispatcher:notifications:new", handleNewDispatcherNotif);
    socket.on("dispatcher:notification:new", handleNewDispatcherNotif);
    socket.on("dispatcher:notifications:stale", handleDispatcherStale);

    return () => {
      if (notifRefreshTimer !== null) {
        window.clearTimeout(notifRefreshTimer);
      }

      socket.emit("unsubscribe:loads");
      socket.off("connect", subscribe);
      socket.off("reconnect", subscribe);
      socket.off("dispatcher:notifications:new", handleNewDispatcherNotif);
      socket.off("dispatcher:notification:new", handleNewDispatcherNotif);
      socket.off("dispatcher:notifications:stale", handleDispatcherStale);
    };
  }, [notificationsActive, refreshNotifCount]);

  // Keep the badge synchronized with the Notifications page.
  useEffect(() => {
    const handler = (event) => {
      const unread = event?.detail?.unread;

      if (Number.isInteger(unread) && unread >= 0) {
        // Do not let an older in-flight request undo this update.
        notifRequestSeqRef.current += 1;
        commitNotifCount(unread);
      }
    };

    window.addEventListener("dispatcher:notifs:unreadCount", handler);

    return () =>
      window.removeEventListener("dispatcher:notifs:unreadCount", handler);
  }, [commitNotifCount]);

  // fechar dropdown ao clicar fora
  useEffect(() => {
    const onClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    if (profileOpen) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [profileOpen]);

  const go = (path) => {
    navigate(path);
    setProfileOpen(false);
  };

  const avatarInitial =
    computeInitial(
      userInfo
    );

  const settingsPaths = [
    "/settings",
    "/general-settings",
    "/company-settings",
    "/profile-settings",
    "/users",
    "/permissions",
  ];
  const isInSettings = settingsPaths.some((p) => location.pathname.startsWith(p));

  return (
    <aside className="sidebar">
      <div className="sidebar-rail">
        {/* TOP: logo */}
        <div className="sidebar-logo" title={t("logo.home")}>
          <img src={logo} alt="LOADEvo" onClick={() => navigate("/loads")} />
        </div>

        {/* CENTER: main pill */}
        <div className="sidebar-pill">
          <nav className="sidebar-menu" aria-label="Sidebar menu">
            <ul>
              {can(userInfo.role, "dashboard") && (
                <li>
                  <NavLink
                    to="/dashboard"
                    title="Dashboard"
                    aria-label="Dashboard"
                    className={({ isActive }) =>
                      `sidebar-btn ${isActive ? "active-link" : ""}`
                    }
                  >
                    <div className="menu-item">
                      <LuLayoutDashboard className="icon" />
                      <span className="menu-label">Dashboard</span>
                    </div>
                  </NavLink>
                </li>
              )}

              {can(userInfo.role, "map") && (
                <li>
                  <NavLink
                    to="/map/live"
                    title={t("menu.map")}
                    aria-label={t("menu.map")}
                    className={() =>
                      `sidebar-btn ${
                        location.pathname.startsWith("/map")
                          ? "active-link"
                          : ""
                      }`
                    }
                  >
                    <div className="menu-item">
                      <LuMap className="icon" />
                      <span className="menu-label">{t("menu.map")}</span>
                    </div>
                  </NavLink>
                </li>
              )}

              {can(userInfo.role, "loads") && (
                <li>
                  <NavLink
                    to="/loads"
                    title={t("menu.loads")}
                    aria-label={t("menu.loads")}
                    className={({ isActive }) =>
                      `sidebar-btn ${isActive ? "active-link" : ""}`
                    }
                    onMouseEnter={() => prefetchLoadsSummaries()}
                    onFocus={() => prefetchLoadsSummaries()}
                    onTouchStart={() => prefetchLoadsSummaries()}
                  >
                    <div className="menu-item">
                      <LuBoxes className="icon" />
                      <span className="menu-label">{t("menu.loads")}</span>
                    </div>
                  </NavLink>
                </li>
              )}

              {can(userInfo.role, "vehicles") && (
                <li>
                  <NavLink
                    to="/trucks"
                    title={t("menu.vehicles")}
                    aria-label={t("menu.vehicles")}
                    className={({ isActive }) =>
                      `sidebar-btn ${isActive ? "active-link" : ""}`
                    }
                    onMouseEnter={() => prefetchVehiclesSummaries()}
                    onFocus={() => prefetchVehiclesSummaries()}
                    onTouchStart={() => prefetchVehiclesSummaries()}
                  >
                    <div className="menu-item">
                      <LuTruck className="icon" />
                      <span className="menu-label">{t("menu.vehicles")}</span>
                    </div>
                  </NavLink>
                </li>
              )}

              {can(userInfo.role, "drivers") && (
                <li>
                  <NavLink
                    to="/drivers"
                    title={t("menu.drivers")}
                    aria-label={t("menu.drivers")}
                    className={({ isActive }) =>
                      `sidebar-btn ${isActive ? "active-link" : ""}`
                    }
                    onMouseEnter={() => prefetchDriversSummaries()}
                    onFocus={() => prefetchDriversSummaries()}
                    onTouchStart={() => prefetchDriversSummaries()}
                  >
                    <div className="menu-item">
                      <LuUser className="icon" />
                      <span className="menu-label">{t("menu.drivers")}</span>
                    </div>
                  </NavLink>
                </li>
              )}

              {can(userInfo.role, "clients") && (
                <li>
                  <NavLink
                    to="/clients"
                    title={t("menu.clients")}
                    aria-label={t("menu.clients")}
                    className={({ isActive }) =>
                      `sidebar-btn ${isActive ? "active-link" : ""}`
                    }
                    onMouseEnter={() => prefetchClientsSummaries()}
                    onFocus={() => prefetchClientsSummaries()}
                    onTouchStart={() => prefetchClientsSummaries()}
                    onMouseDown={() => prefetchClientsSummaries()}
                    onPointerDown={() => prefetchClientsSummaries()}
                  >
                    <div className="menu-item">
                      <LuHandshake className="icon" />
                      <span className="menu-label">{t("menu.clients")}</span>
                    </div>
                  </NavLink>
                </li>
              )}

              {can(userInfo.role, "places") && (
                <li>
                  <NavLink
                    to="/places"
                    title={t("menu.places")}
                    aria-label={t("menu.places")}
                    className={({ isActive }) =>
                      `sidebar-btn ${isActive ? "active-link" : ""}`
                    }
                    onMouseEnter={() => prefetchPlacesList()}
                    onFocus={() => prefetchPlacesList()}
                    onTouchStart={() => prefetchPlacesList()}
                    onMouseDown={() => prefetchPlacesList()}
                    onPointerDown={() => prefetchPlacesList()}
                  >
                    <div className="menu-item">
                      <LuMapPin className="icon" />
                      <span className="menu-label">{t("menu.places")}</span>
                    </div>
                  </NavLink>
                </li>
              )}

              {can(userInfo.role, "invoices") && (
                <li>
                  <NavLink
                    to="/invoices"
                    title={t("menu.billing")}
                    aria-label={t("menu.billing")}
                    className={({ isActive }) =>
                      `sidebar-btn ${isActive ? "active-link" : ""}`
                    }
                    onMouseEnter={() => {
                      prefetchBillingSummaries();
                      prefetchInvoicesList();
                    }}
                    onFocus={() => {
                      prefetchBillingSummaries();
                      prefetchInvoicesList();
                    }}
                    onTouchStart={() => {
                      prefetchBillingSummaries();
                      prefetchInvoicesList();
                    }}
                    onMouseDown={() => prefetchInvoicesList()}
                    onPointerDown={() => prefetchInvoicesList()}
                  >
                    <div className="menu-item">
                      <LuFileText className="icon" />
                      <span className="menu-label">{t("menu.billing")}</span>
                    </div>
                  </NavLink>
                </li>
              )}

              {[
                "accountant",
                "admin",
                "dispatcher",
              ].includes(
                String(userInfo.role || "")
                  .trim()
                  .toLowerCase()
              ) &&
                (
                  (
                    typeof window !== "undefined" &&
                    String(window.location.hostname || "")
                      .trim()
                      .toLowerCase() === "test-app.loadevo.com"
                  ) ||
                  String(api?.defaults?.baseURL || "")
                    .trim()
                    .toLowerCase()
                    .includes("test-api.loadevo.com")
                ) && (
                  <li>
                    <NavLink
                      to="/fiscal-certification"
                      title="Certificação Fiscal"
                      aria-label="Certificação Fiscal"
                      className={({ isActive }) =>
                        `sidebar-btn ${isActive ? "active-link" : ""}`
                      }
                    >
                      <div className="menu-item">
                        <LuFileText className="icon" />
                        <span className="menu-label">
                          Certificação Fiscal
                        </span>
                      </div>
                    </NavLink>
                  </li>
                )}
            </ul>
          </nav>
        </div>

        {/* BOTTOM: small pill actions (notif + profile) */}
        <div className="sidebar-bottom" ref={profileRef}>
          {hasAnyPersonnelView && (
            <div className="sidebar-bottom-feature">
              <NavLink
                to={firstAllowedPersonnelPath}
                title="Personnel"
                aria-label="Personnel"
                className={() =>
                  `sidebar-btn sidebar-feature-btn ${
                    location.pathname.startsWith("/personnel")
                      ? "active-link"
                      : ""
                  }`
                }
                onMouseEnter={() =>
                  prefetchPersonnelFoundation({
                    scope: getPersonnelPrefetchScopeFromPath(
                      firstAllowedPersonnelPath
                    ),
                  })
                }
                onFocus={() =>
                  prefetchPersonnelFoundation({
                    scope: getPersonnelPrefetchScopeFromPath(
                      firstAllowedPersonnelPath
                    ),
                  })
                }
                onTouchStart={() =>
                  prefetchPersonnelFoundation({
                    scope: getPersonnelPrefetchScopeFromPath(
                      firstAllowedPersonnelPath
                    ),
                  })
                }
                onMouseDown={() =>
                  prefetchPersonnelFoundation({
                    scope: getPersonnelPrefetchScopeFromPath(
                      firstAllowedPersonnelPath
                    ),
                  })
                }
                onPointerDown={() =>
                  prefetchPersonnelFoundation({
                    scope: getPersonnelPrefetchScopeFromPath(
                      firstAllowedPersonnelPath
                    ),
                  })
                }
              >
                <div className="menu-item menu-item--bottom">
                  <LuCalendarDays className="icon" />

                  {canViewPersonnelRequests &&
                    personnelPendingCount > 0 && (
                      <span
                        className="notif-badge personnel-pending-badge"
                        aria-hidden="true"
                      >
                        {personnelPendingCount > 99
                          ? "99+"
                          : personnelPendingCount}
                      </span>
                    )}

                  <span className="menu-label">
                    Personnel
                  </span>
                </div>
              </NavLink>
            </div>
          )}

          <div className="sidebar-bottom-divider" />

          <div className="sidebar-bottom-pill">

            <div className="sidebar-theme-control">
              <button
                type="button"
                role="switch"
                aria-label="Modo escuro"
                aria-checked={isDarkMode}
                title={
                  isDarkMode
                    ? "Ativar modo claro"
                    : "Ativar modo escuro"
                }
                className={`sidebar-theme-switch ${
                  isDarkMode ? "is-dark" : ""
                }`}
                onClick={toggleSidebarTheme}
              >
                <span
                  className="sidebar-theme-switch__icon sidebar-theme-switch__icon--sun"
                  aria-hidden="true"
                >
                  <LuSun />
                </span>

                <span
                  className="sidebar-theme-switch__icon sidebar-theme-switch__icon--moon"
                  aria-hidden="true"
                >
                  <LuMoon />
                </span>
              </button>
            </div>

            <NavLink
              to="/notifications"
              title={t("menu.notifications") || "Notifications"}
              aria-label={t("menu.notifications") || "Notifications"}
              className={({ isActive }) =>
                `sidebar-btn ${isActive ? "active-link" : ""}`
              }
            >
              <div className="menu-item menu-item--bottom">
                <LuBell className="icon" />
                  {notificationsActive && notifCount > 0 && (
                    <span className="notif-badge" aria-hidden="true">
                      {notifCount > 99 ? "99+" : notifCount}
                    </span>
                  )}
                <span className="menu-label">
                  {t("menu.notifications") || "Notifications"}
                </span>
              </div>
            </NavLink>

            <button
              className={`sidebar-btn profile-trigger ${
                isInSettings ? "active-link" : ""
              }`}
              onClick={() => setProfileOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={profileOpen}
              title={t("profile.settings")}
              type="button"
            >
              <div className="avatar-circle">
                {userInfo.avatarUrl ? (
                  <img
                    src={userInfo.avatarUrl}
                    alt={avatarInitial}
                    onError={() =>
                      setUserInfo((prev) => ({ ...prev, avatarUrl: null }))
                    }
                  />
                ) : (
                  avatarInitial
                )}
              </div>
            </button>
          </div>

          {profileOpen && (
            <div className="profile-menu" role="menu">
              <div className="profile-header">
                <div className="avatar-circle avatar-sm">
                  {userInfo.avatarUrl ? (
                    <img
                      src={userInfo.avatarUrl}
                      alt={avatarInitial}
                      onError={() =>
                        setUserInfo((prev) => ({ ...prev, avatarUrl: null }))
                      }
                    />
                  ) : (
                    avatarInitial
                  )}
                </div>
                <div className="profile-id">
                  <div className="profile-name">
                    {userInfo.name || t("profile.default_user")}
                  </div>
                  {!!userInfo.email && (
                    <div className="profile-email" title={userInfo.email}>
                      {userInfo.email}
                    </div>
                  )}
                </div>
              </div>

              <div className="profile-divider" />

              <button
                className={`profile-item ${isInSettings ? "profile-item--active" : ""}`}
                onClick={() => go("/settings")}
                role="menuitem"
                type="button"
              >
                <LuSettings className="profile-ico" />
                <span>{t("profile.settings")}</span>
              </button>

              <button
                className="profile-item"
                onClick={() => {
                  setProfileOpen(false);
                  hardLogout({ redirectTo: "/login" });
                }}
                role="menuitem"
                type="button"
              >
                <LuLogOut className="profile-ico" />
                <span>{t("profile.logout")}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
